import { useState } from 'react';
import { ChevronDownIcon, Loader2, Trash2 } from 'lucide-react';
import type { Table as TanTable } from '@tanstack/react-table';

import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

export interface BulkAction {
  label: string;
  icon?: React.ReactNode;
  variant?: 'default' | 'destructive';
  confirm?: {
    title: string;
    description: string;
  };
  onExecute: () => Promise<void>;
}

interface BulkActionsDropdownProps {
  selectedCount: number;
  actions: BulkAction[];
}

export function BulkActionsDropdown({ selectedCount, actions }: BulkActionsDropdownProps) {
  const [confirmAction, setConfirmAction] = useState<BulkAction | null>(null);
  const [isExecuting, setIsExecuting] = useState(false);

  const handleAction = (action: BulkAction) => {
    if (action.confirm) {
      setConfirmAction(action);
      return;
    }
    executeAction(action);
  };

  const executeAction = async (action: BulkAction) => {
    setIsExecuting(true);
    try {
      await action.onExecute();
    } finally {
      setIsExecuting(false);
      setConfirmAction(null);
    }
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button size="sm" variant="outline" disabled={selectedCount === 0 || isExecuting}>
            {isExecuting ? (
              <Loader2 className="animate-spin" />
            ) : (
              <>
                Actions
                {selectedCount > 0 && (
                  <span className="ml-1.5 inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-primary px-1.5 text-[11px] font-medium text-primary-foreground">
                    {selectedCount}
                  </span>
                )}
                <ChevronDownIcon />
              </>
            )}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-48">
          {actions.map((action) => (
            <DropdownMenuItem
              key={action.label}
              onClick={() => handleAction(action)}
              className={action.variant === 'destructive' ? 'text-destructive focus:text-destructive' : ''}
            >
              {action.icon}
              {action.label}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>

      <AlertDialog open={!!confirmAction} onOpenChange={() => setConfirmAction(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{confirmAction?.confirm?.title}</AlertDialogTitle>
            <AlertDialogDescription>
              {confirmAction?.confirm?.description}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isExecuting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={isExecuting}
              className={confirmAction?.variant === 'destructive' ? 'bg-destructive text-destructive-foreground hover:bg-destructive/90' : ''}
              onClick={(e) => {
                e.preventDefault();
                if (confirmAction) executeAction(confirmAction);
              }}
            >
              {isExecuting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Confirm
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

/** Renders a BulkActionsDropdown wired to a TanStack table instance */
export function TableBulkActions<T>({
  table,
  actions,
}: {
  table: TanTable<T>;
  actions: (rows: T[]) => BulkAction[];
}) {
  const selectedRows = table.getFilteredSelectedRowModel().rows.map((r) => r.original);
  return (
    <BulkActionsDropdown
      selectedCount={selectedRows.length}
      actions={actions(selectedRows)}
    />
  );
}

/** Convenience helper for a bulk delete action */
export function bulkDeleteAction<T>(opts: {
  rows: T[];
  entityName: string;
  getId: (row: T) => string;
  onDelete: (ids: string[]) => Promise<void>;
}): BulkAction {
  const count = opts.rows.length;
  const plural = count === 1 ? opts.entityName : `${opts.entityName}s`;
  return {
    label: `Delete ${count} ${plural}`,
    icon: <Trash2 />,
    variant: 'destructive',
    confirm: {
      title: `Delete ${count} ${plural}?`,
      description: `This action cannot be undone. ${count} ${plural} will be permanently deleted.`,
    },
    onExecute: async () => {
      const ids = opts.rows.map(opts.getId);
      await opts.onDelete(ids);
    },
  };
}
